// O2D.P-PRD-018 §TR6
// Per-object localStorage draft store with a SINGLE debounced-save timer
// ownership model. There is exactly one place that owns the pending debounced
// `save` timer (this store, keyed per `sid`) and exactly one cancel path
// (`cancelPending`). `saveNow`, `clear` and the element's disconnect timer-clear
// (TR9) all void the pending timer through that single path before writing or
// removing — so a later-elapsing trailing timer can neither overwrite an
// authoritative write (R3-B1) nor resurrect a cleared key.
//
// Public API surface is intentionally the 5 methods below (KISS, TR6). `keyOf`
// and `write` are private implementation helpers.

import type { RichTextDocument } from "../types/rich-text-document.type.js";

const KEY_PREFIX = "o2d.rte-draft.";
const DEBOUNCE_MS = 500; // TR6 draft-save debounce window (OQ3 default)

export class DraftStore {
  // The ONE debounced-save timer per object SID (TR6 ownership model (a)/(d)).
  // The element holds no competing draft timer.
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();

  /**
   * Debounced write: (re-)arms the per-SID timer; the last doc within the window
   * wins. Rapid `save` calls coalesce onto a single timer (TR6).
   */
  save(sid: string, doc: RichTextDocument): void {
    this.cancelPending(sid);
    const handle = setTimeout(() => {
      this.timers.delete(sid);
      this.write(sid, doc);
    }, DEBOUNCE_MS);
    this.timers.set(sid, handle);
  }

  /**
   * Synchronous write (debounce bypass). Voids any pending debounced timer FIRST
   * (via `cancelPending`) so a later-firing timer cannot overwrite this
   * authoritative doc with stale content (R3-B1). Used by the markSaved
   * dirty-branch (TR5), the disconnect flush (TR9) and — since 2026-09-18 —
   * for the FIRST dirty keystroke of an edit session: the host re-reads the
   * draft synchronously when `rich-text-changed` fires, so that opening
   * write must not lag behind the event. Every further keystroke of the
   * session goes through the debounced `save`.
   */
  saveNow(sid: string, doc: RichTextDocument): void {
    this.cancelPending(sid);
    this.write(sid, doc);
  }

  /**
   * Reads and parses ONLY this SID's key. Absent or corrupt/unparseable JSON
   * resolves to `undefined` (defensive) rather than throwing.
   */
  load(sid: string): RichTextDocument | undefined {
    const raw = localStorage.getItem(this.keyOf(sid));
    if (raw === null) {
      return undefined;
    }
    try {
      return JSON.parse(raw) as RichTextDocument;
    } catch {
      return undefined;
    }
  }

  /**
   * Voids the pending debounced timer FIRST (so a running 500ms timer cannot
   * re-write the just-cleared draft — a resurrected ghost), then removes the key.
   */
  clear(sid: string): void {
    this.cancelPending(sid);
    try {
      localStorage.removeItem(this.keyOf(sid));
    } catch (error) {
      console.warn(`[DraftStore] failed to clear draft for "${sid}"`, error);
    }
  }

  /**
   * The single cancel path (TR6 model (d)): cancels the pending debounced `save`
   * timer for this SID. No-op when no timer is armed. `clear`, `saveNow` and the
   * disconnect timer-clear all delegate here — there is no second cancel site.
   */
  cancelPending(sid: string): void {
    const handle = this.timers.get(sid);
    if (handle === undefined) {
      return;
    }
    clearTimeout(handle);
    this.timers.delete(sid);
  }

  /** Per-SID localStorage key `o2d.rte-draft.<sid>`. */
  private keyOf(sid: string): string {
    return `${KEY_PREFIX}${sid}`;
  }

  /**
   * Persists the doc under the SID key. QuotaExceeded / disabled storage is
   * swallowed with a `console.warn` (no silent empty catch) so editing continues
   * undisturbed; the draft-save is simply skipped for that write.
   *
   * The write goes through `Storage.prototype.setItem` explicitly rather than
   * `localStorage.setItem`. In real browsers the two are identical (localStorage
   * IS a Storage instance whose `setItem` lives on `Storage.prototype`), but it
   * keeps the QuotaExceeded failure path observable at the standard
   * `Storage.prototype.setItem` boundary — which is exactly where tests (and any
   * quota instrumentation) hook it.
   */
  private write(sid: string, doc: RichTextDocument): void {
    try {
      Storage.prototype.setItem.call(
        localStorage,
        this.keyOf(sid),
        JSON.stringify(doc),
      );
    } catch (error) {
      console.warn(`[DraftStore] failed to persist draft for "${sid}"`, error);
    }
  }
}
